import { Flex, MultiSelect, Text } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { ResourceAvatar } from '@medplum/react';
import { useMemo } from 'react';

interface PractitionerMultiSelectProps {
  valueIds: string[]; // selected Practitioner ids
  onChangeIds: (ids: string[]) => void;
  practitioners?: Practitioner[];
  label?: string;
  placeholder?: string;
}

interface PractitionerOption {
  value: string;
  label: string;
  practitioner: Practitioner;
}

interface PractitionerSelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  practitioner: Practitioner;
  roleLabel?: string;
}

function PractitionerSelectItem({ label, practitioner, roleLabel, ...others }: PractitionerSelectItemProps) {
  return (
    <Flex gap="xs" align="center" direction="row" {...others}>
      <ResourceAvatar value={practitioner} radius="xl" />
      <Flex direction="column">
        <Text>{label}</Text>
        {roleLabel && (
          <Text size="xs" c="dimmed">
            {roleLabel}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export function PractitionerMultiSelect({
  valueIds,
  onChangeIds,
  practitioners,
  label,
  placeholder,
}: PractitionerMultiSelectProps) {
  const data: PractitionerOption[] = useMemo(
    () =>
      (practitioners ?? [])
        .map((p) => ({
          value: p.id ?? '',
          label: formatHumanName(p.name?.[0]) ?? '',
          practitioner: p,
        }))
        .filter((option) => option.value && option.label),
    [practitioners]
  );

  return (
    <MultiSelect
      label={label}
      placeholder={placeholder ?? 'All providers'}
      data={data}
      value={valueIds}
      onChange={onChangeIds}
      searchable
      clearable
      renderOption={(input) => {
        const option = input.option as PractitionerOption;
        return (
          <PractitionerSelectItem
            label={option.label}
            practitioner={option.practitioner}
            roleLabel={(option.practitioner as any).roleLabel}
          />
        );
      }}
    />
  );
}
